import { useRouter } from 'expo-router';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { StyleSheet, type UnistylesThemes } from 'react-native-unistyles';
import { gql, useMutation } from 'urql';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import Text from '@/components/Unistyles/Text';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import useUserId from '@/hooks/useUserId';
import {
  resetWelcomeInviteDraft,
  resetWelcomeOrganizationDraft,
  resetWelcomeTeamDraft,
  type WelcomeFormValues,
} from '@/layouts/Welcome/form';
import useOnboardingStore, {
  type OnboardingPersona,
} from '@/stores/useOnboardingStore';

type UpdateWelcomePersonaData = {
  update_person_by_pk?: {
    id: string;
    persona: OnboardingPersona | null;
  } | null;
};

type UpdateWelcomePersonaVariables = {
  id: string;
  persona: OnboardingPersona;
};

type PersonaTone = 'primary' | 'success' | 'info' | 'warning';
type WelcomeTheme = UnistylesThemes['dark'];

const UpdateWelcomePersona = gql`
  mutation UpdateWelcomePersona($id: String!, $persona: person_persona_enum!) {
    update_person_by_pk(pk_columns: { id: $id }, _set: { persona: $persona }) {
      id
      persona
    }
  }
`;

const PERSONA_OPTIONS: Array<{
  value: OnboardingPersona;
  title: string;
  subtitle: string;
  icon: ComponentProps<typeof Icon>['name'];
  tone: PersonaTone;
}> = [
  {
    value: 'player',
    title: 'Regular Member',
    subtitle: 'Join a team or organization as a player or parent.',
    icon: 'heart-circle-outline',
    tone: 'info',
  },
  {
    value: 'team_manager',
    title: 'Team Organizer',
    subtitle: 'Create or join one team you help manage.',
    icon: 'people-circle-outline',
    tone: 'success',
  },
  {
    value: 'organization_manager',
    title: 'League Organizer',
    subtitle: 'Create an organization, league, season, teams, and games.',
    icon: 'trophy-outline',
    tone: 'primary',
  },
];

export default function WelcomePersona() {
  const userId = useUserId();
  const router = useRouter();
  const { setValue } = useFormContext<WelcomeFormValues>();
  const setPersona = useOnboardingStore((state) => state.setPersona);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittingPersona, setSubmittingPersona] =
    useState<OnboardingPersona | null>(null);
  const [, updatePersona] = useMutation<
    UpdateWelcomePersonaData,
    UpdateWelcomePersonaVariables
  >(UpdateWelcomePersona);

  const selectPersona = async (persona: OnboardingPersona) => {
    if (submittingPersona) {
      return;
    }

    setSubmittingPersona(persona);
    setValue('persona', persona, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setPersona(persona);
    setErrorMessage(null);
    resetWelcomeInviteDraft(setValue);
    resetWelcomeTeamDraft(setValue);
    resetWelcomeOrganizationDraft(setValue);

    const { error } = await updatePersona({
      id: userId,
      persona,
    });

    if (error) {
      setErrorMessage('Unable to save your role. Try again.');
      setSubmittingPersona(null);
      return;
    }

    setSubmittingPersona(null);

    if (persona === 'organization_manager') {
      router.navigate('/welcome/org');
      return;
    }

    if (persona === 'team_manager') {
      router.navigate('/welcome/team');
      return;
    }

    router.navigate('/welcome/join');
  };

  return (
    <WelcomeStepFrame
      title="Who are you?"
      subtitle="We'll shape the rest of the setup process around your needs."
    >
      <WelcomeStepScrollContent>
        <Box w="full" space="md">
          {PERSONA_OPTIONS.map((option) => {
            const isSubmitting = submittingPersona === option.value;

            return (
              <Pressable
                accessibilityLabel={option.title}
                accessibilityRole="button"
                disabled={Boolean(submittingPersona)}
                key={option.value}
                onPress={() => void selectPersona(option.value)}
                styleOverride={({ pressed }) =>
                  styles.option(option.tone, pressed || isSubmitting)
                }
              >
                <Box direction="horizontal" alignItems="center" space="md">
                  <Box center style={styles.iconBadge}>
                    <Icon name={option.icon} size="3xl" variant={option.tone} />
                  </Box>
                  <Box flex space="xs">
                    <Text flexShrink={1} size="lg" bold>
                      {option.title}
                    </Text>
                    <Text size="sm" variant="typographySecondary">
                      {option.subtitle}
                    </Text>
                  </Box>
                  <Icon
                    name="chevron-forward"
                    size="sm"
                    variant="typographySecondary"
                  />
                </Box>
              </Pressable>
            );
          })}
        </Box>

        {errorMessage ? (
          <Text variant="error" center selectable>
            {errorMessage}
          </Text>
        ) : null}
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}

function getToneColor(theme: WelcomeTheme, tone: PersonaTone) {
  switch (tone) {
    case 'success':
      return theme.colors.success;
    case 'info':
      return theme.colors.info;
    case 'warning':
      return theme.colors.warning;
    default:
      return theme.colors.primary;
  }
}

const styles = StyleSheet.create((theme) => ({
  iconBadge: {
    alignItems: 'center',
    height: theme.size(9),
    justifyContent: 'center',
    width: theme.size(9),
    _web: {
      _hover: {
        opacity: 0.9,
      },
    },
  },
  option: (tone: PersonaTone, active: boolean) => ({
    backgroundColor: active
      ? theme.colors.surfaceSecondary
      : theme.colors.surface,
    borderColor: active ? getToneColor(theme, tone) : theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    padding: theme.space('lg'),
    width: '100%',
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
        borderColor: getToneColor(theme, tone),
      },
    },
  }),
}));
